import { Request, Response } from "express";
import mongoose from "mongoose";

import { Device } from "../models";
import { fcmService } from "../services/fcm-service";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

/**
 * Device Controller
 * Manages device registration, FCM token updates, and device lifecycle
 */
class DeviceController {
  /**
   * Register or update device with FCM token
   * @route POST /api/v1/devices/register
   * @access Private (All authenticated users)
   */
  async registerDevice(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const {
        deviceToken,
        deviceType,
        fcmToken,
        deviceName,
        deviceModel,
        osVersion,
        appVersion,
      } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      // Validate required fields
      if (!deviceToken || !deviceType) {
        res.status(400).json({
          success: false,
          message: "Device token and device type are required",
        } as APIResponse);
        return;
      }

      // Validate device type
      const validDeviceTypes = ["ios", "android", "web"];
      if (!validDeviceTypes.includes(deviceType)) {
        res.status(400).json({
          success: false,
          message: "Invalid device type. Must be ios, android, or web",
        } as APIResponse);
        return;
      }

      // Validate FCM token if provided
      if (fcmToken && fcmService.isInitialized()) {
        const isValid = await fcmService.validateToken(fcmToken);
        if (!isValid) {
          logger.warn(
            `Invalid FCM token provided: ${fcmToken.slice(0, 20)}...`,
          );
          // Continue anyway - token might be valid but validation failed
        }
      }

      // Check if device already exists
      let device = await Device.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        deviceToken,
      });

      if (device) {
        // Update existing device
        device.deviceType = deviceType;
        device.fcmToken = fcmToken || device.fcmToken;
        device.deviceName = deviceName || device.deviceName;
        device.deviceModel = deviceModel || device.deviceModel;
        device.osVersion = osVersion || device.osVersion;
        device.appVersion = appVersion || device.appVersion;
        device.isActive = true;
        device.lastActiveAt = new Date();

        await device.save();

        logger.info(`Device updated for user: ${userId}`);

        res.status(200).json({
          success: true,
          message: "Device updated successfully",
          data: {
            device: {
              id: device.id,
              deviceType: device.deviceType,
              deviceName: device.deviceName,
              isActive: device.isActive,
              lastActiveAt: device.lastActiveAt,
            },
          },
        } as APIResponse);
      } else {
        // Create new device
        device = new Device({
          userId: new mongoose.Types.ObjectId(userId),
          deviceToken,
          deviceType,
          fcmToken,
          deviceName,
          deviceModel,
          osVersion,
          appVersion,
          isActive: true,
          lastActiveAt: new Date(),
        });

        await device.save();

        logger.info(`New device registered for user: ${userId}`);

        res.status(201).json({
          success: true,
          message: "Device registered successfully",
          data: {
            device: {
              id: device.id,
              deviceType: device.deviceType,
              deviceName: device.deviceName,
              isActive: device.isActive,
              lastActiveAt: device.lastActiveAt,
            },
          },
        } as APIResponse);
      }

      // Cleanup old devices (keep only last 5 active devices per user)
      await Device.deactivateOldDevices(new mongoose.Types.ObjectId(userId), 5);
    } catch (error: any) {
      logger.error("Register device error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to register device",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Update FCM token for existing device
   * @route PUT /api/v1/devices/:deviceId/fcm-token
   * @access Private (All authenticated users)
   */
  async updateFCMToken(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { deviceId } = req.params;
      const { fcmToken } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (!fcmToken) {
        res.status(400).json({
          success: false,
          message: "FCM token is required",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(deviceId)) {
        res.status(400).json({
          success: false,
          message: "Invalid device ID",
        } as APIResponse);
        return;
      }

      // Find device
      const device = await Device.findOne({
        _id: new mongoose.Types.ObjectId(deviceId),
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!device) {
        res.status(404).json({
          success: false,
          message: "Device not found",
        } as APIResponse);
        return;
      }

      // Validate FCM token if FCM is initialized
      if (fcmService.isInitialized()) {
        const isValid = await fcmService.validateToken(fcmToken);
        if (!isValid) {
          logger.warn(
            `Invalid FCM token provided: ${fcmToken.slice(0, 20)}...`,
          );
          // Continue anyway - token might be valid but validation failed
        }
      }

      // Update FCM token
      device.fcmToken = fcmToken;
      device.lastActiveAt = new Date();
      await device.save();

      logger.info(`FCM token updated for device: ${deviceId}`);

      res.status(200).json({
        success: true,
        message: "FCM token updated successfully",
        data: {
          device: {
            id: device.id,
            deviceType: device.deviceType,
            deviceName: device.deviceName,
            isActive: device.isActive,
            lastActiveAt: device.lastActiveAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Update FCM token error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to update FCM token",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all devices for authenticated user
   * @route GET /api/v1/devices
   * @access Private (All authenticated users)
   */
  async getUserDevices(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { includeInactive } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const query: any = {
        userId: new mongoose.Types.ObjectId(userId),
      };

      if (includeInactive !== "true") {
        query.isActive = true;
      }

      const devices = await Device.find({ ...query }).sort({
        lastActiveAt: -1,
      });

      res.status(200).json({
        success: true,
        message: "Devices retrieved successfully",
        data: {
          devices: devices.map(device => ({
            id: device.id,
            deviceType: device.deviceType,
            deviceName: device.deviceName,
            deviceModel: device.deviceModel,
            osVersion: device.osVersion,
            appVersion: device.appVersion,
            isActive: device.isActive,
            hasFCMToken: !!device.fcmToken,
            lastActiveAt: device.lastActiveAt,
            createdAt: device.createdAt,
          })),
          total: devices.length,
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get user devices error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve devices",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Deactivate a device
   * @route DELETE /api/v1/devices/:deviceId
   * @access Private (All authenticated users)
   */
  async deactivateDevice(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { deviceId } = req.params;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(deviceId)) {
        res.status(400).json({
          success: false,
          message: "Invalid device ID",
        } as APIResponse);
        return;
      }

      const device = await Device.findOne({
        _id: new mongoose.Types.ObjectId(deviceId),
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!device) {
        res.status(404).json({
          success: false,
          message: "Device not found",
        } as APIResponse);
        return;
      }

      await device.deactivate();

      logger.info(`Device deactivated: ${deviceId}`);

      res.status(200).json({
        success: true,
        message: "Device deactivated successfully",
      } as APIResponse);
    } catch (error: any) {
      logger.error("Deactivate device error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to deactivate device",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Test push notification (for testing purposes)
   * @route POST /api/v1/devices/test-notification
   * @access Private (All authenticated users)
   */
  async testNotification(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      // Get user's active devices with FCM tokens
      const devices = await Device.find({
        userId: new mongoose.Types.ObjectId(userId),
        isActive: true,
        fcmToken: { $exists: true, $ne: null },
      });

      if (devices.length === 0) {
        res.status(404).json({
          success: false,
          message: "No active devices with FCM tokens found",
        } as APIResponse);
        return;
      }

      const fcmTokens = devices
        .map(device => device.fcmToken)
        .filter(
          (token): token is string => token !== null && token !== undefined,
        );

      // Send test notification
      const result = await fcmService.sendToMultipleDevices(
        fcmTokens,
        {
          title: "Test Notification",
          body: "This is a test push notification from Covalent",
        },
        {
          type: "test",
          timestamp: new Date().toISOString(),
        },
        {
          priority: "high",
          sound: "default",
        },
      );

      logger.info(
        `Test notification sent: ${result.successCount} success, ${result.failureCount} failure`,
      );

      res.status(200).json({
        success: true,
        message: "Test notification sent",
        data: {
          devicesCount: devices.length,
          successCount: result.successCount,
          failureCount: result.failureCount,
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Test notification error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to send test notification",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get FCM service status
   * @route GET /api/v1/devices/fcm-status
   * @access Private (Admin only)
   */
  async getFCMStatus(req: AuthRequest, res: Response): Promise<void> {
    try {
      const role = req.user?.role;

      if (role !== "admin") {
        res.status(403).json({
          success: false,
          message: "Access denied. Admin only.",
        } as APIResponse);
        return;
      }

      const isInitialized = fcmService.isInitialized();

      res.status(200).json({
        success: true,
        message: "FCM status retrieved",
        data: {
          initialized: isInitialized,
          status: isInitialized ? "active" : "inactive",
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get FCM status error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to get FCM status",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }
}

export const deviceController = new DeviceController();
